import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import leavePolicyApi, { PublicHoliday } from '../../../network/leavePolicy';
import { useOffices } from '../../../hooks/useOrganisationLists';
import { useToast } from '../../../utils/useToast';
import { Button } from '../../../ui/Button';

const WEEKDAYS = [
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
  { value: 7, label: 'Sun' },
];

function errorMessage(error: any, fallback: string) {
  const message = error?.response?.data?.message;
  return typeof message === 'string' && message ? message : fallback;
}

function formatDate(iso: string) {
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/** Office scope picker: every office, or the ones ticked. */
function OfficeScope({
  value,
  onChange,
  idPrefix,
}: {
  value: string[];
  onChange(ids: string[]): void;
  idPrefix: string;
}) {
  const { data: offices = [] } = useOffices();
  if (!offices.length) return null;
  return (
    <fieldset className="flex flex-col gap-1 text-sm">
      <legend className="mb-1">Offices</legend>
      <label className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={value.length === 0}
          onChange={() => onChange([])}
        />
        All offices
      </label>
      {offices.map((office) => (
        <label key={office.id} className="flex items-center gap-2" htmlFor={`${idPrefix}-${office.id}`}>
          <input
            id={`${idPrefix}-${office.id}`}
            type="checkbox"
            checked={value.includes(String(office.id))}
            onChange={(e) =>
              onChange(
                e.target.checked
                  ? [...value, String(office.id)]
                  : value.filter((id) => id !== String(office.id)),
              )
            }
          />
          {office.name}
        </label>
      ))}
    </fieldset>
  );
}

/**
 * Settings > Holidays & working days: public holidays per office (added
 * one by one or from a bundled template) and the company's working
 * weekdays. Neither counts as leave when a request is worked out.
 */
export function HolidaysSettings() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const { data: offices = [] } = useOffices();

  const holidaysQuery = useQuery(
    ['holidays', year],
    () =>
      leavePolicyApi.getHolidays({ from: `${year}-01-01`, to: `${year}-12-31` }),
    { retry: false },
  );
  const { data: templates = [] } = useQuery(
    ['holiday-templates'],
    () => leavePolicyApi.getHolidayTemplates(),
    { retry: false },
  );
  const settingsQuery = useQuery(
    ['leave-settings'],
    () => leavePolicyApi.getLeaveSettings(),
    { retry: false },
  );

  const [workingDays, setWorkingDays] = useState<number[]>([1, 2, 3, 4, 5]);
  useEffect(() => {
    if (settingsQuery.data?.working_days) {
      setWorkingDays(settingsQuery.data.working_days);
    }
  }, [settingsQuery.data]);

  const [templateId, setTemplateId] = useState('');
  const [templateOffices, setTemplateOffices] = useState<string[]>([]);
  const [newHoliday, setNewHoliday] = useState({
    date: '',
    name: '',
    half_day: false,
    office_ids: [] as string[],
  });

  const selectedTemplate = templates.find((t) => t.id === templateId);
  const officeNames = useMemo(
    () => new Map(offices.map((office) => [String(office.id), office.name])),
    [offices],
  );

  const refreshHolidays = () => queryClient.invalidateQueries('holidays');

  const addFromTemplate = useMutation(
    () =>
      leavePolicyApi.addHolidaysFromTemplate({
        template: templateId,
        year,
        ...(templateOffices.length
          ? { office_ids: templateOffices }
          : { all_offices: true }),
      }),
    {
      onSuccess: (result) => {
        toast({
          title: `Added ${result?.added ?? 0} holidays`,
          description: result?.skipped
            ? `${result.skipped} were already in your list.`
            : undefined,
          variant: 'success',
        });
        refreshHolidays();
      },
      onError: (error) =>
        void toast({
          title: 'Holidays not added',
          description: errorMessage(error, 'Please try again.'),
          variant: 'error',
        }),
    },
  );

  const addHoliday = useMutation(
    () =>
      leavePolicyApi.createHoliday({
        date: newHoliday.date,
        name: newHoliday.name.trim(),
        half_day: newHoliday.half_day,
        ...(newHoliday.office_ids.length
          ? { office_ids: newHoliday.office_ids }
          : { all_offices: true }),
      }),
    {
      onSuccess: () => {
        toast({ title: 'Holiday added', variant: 'success' });
        setNewHoliday({ date: '', name: '', half_day: false, office_ids: [] });
        refreshHolidays();
      },
      onError: (error) =>
        void toast({
          title: 'Holiday not added',
          description: errorMessage(error, 'Please try again.'),
          variant: 'error',
        }),
    },
  );

  const removeHoliday = useMutation(
    (holiday: PublicHoliday) => leavePolicyApi.deleteHoliday(holiday.id),
    {
      onSuccess: () => {
        toast({ title: 'Holiday removed', variant: 'success' });
        refreshHolidays();
      },
      onError: (error) =>
        void toast({
          title: 'Holiday not removed',
          description: errorMessage(error, 'Please try again.'),
          variant: 'error',
        }),
    },
  );

  const saveWorkingDays = useMutation(
    () => leavePolicyApi.updateLeaveSettings({ working_days: workingDays }),
    {
      onSuccess: () => {
        toast({ title: 'Working days saved', variant: 'success' });
        queryClient.invalidateQueries('leave-settings');
      },
      onError: (error) =>
        void toast({
          title: 'Working days not saved',
          description: errorMessage(error, 'Please try again.'),
          variant: 'error',
        }),
    },
  );

  const holidays = holidaysQuery.data ?? [];

  return (
    <div className="flex flex-col gap-8">
      <section className="flex flex-col gap-3">
        <div className="flex items-center justify-between gap-3">
          <h3 className="text-sm font-semibold">Public holidays</h3>
          <label className="flex items-center gap-2 text-sm">
            Year
            <select
              aria-label="Holiday year"
              className="rounded-md border border-gray-300 px-2 py-1"
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
            >
              {[currentYear - 1, currentYear, currentYear + 1, currentYear + 2].map(
                (y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ),
              )}
            </select>
          </label>
        </div>

        {holidaysQuery.isLoading ? (
          <p className="text-sm text-gray-500">Loading holidays...</p>
        ) : holidaysQuery.isError ? (
          <p role="alert" className="text-sm text-red-600">
            Holidays could not be loaded.
          </p>
        ) : holidays.length === 0 ? (
          <p className="text-sm text-gray-500">
            No public holidays for {year}. Add them from a template or one by one
            below.
          </p>
        ) : (
          <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200">
            {holidays.map((holiday) => (
              <li
                key={holiday.id}
                className="flex items-center justify-between gap-3 px-3 py-2 text-sm"
              >
                <span>
                  <span className="font-medium">{holiday.name}</span>
                  <span className="ml-2 text-gray-500">
                    {formatDate(holiday.date)}
                    {holiday.half_day ? ' (half day)' : ''}
                  </span>
                  <span className="ml-2 text-xs text-gray-500">
                    {holiday.all_offices
                      ? 'All offices'
                      : holiday.office_ids
                          .map((id) => officeNames.get(String(id)) ?? id)
                          .join(', ')}
                  </span>
                </span>
                <Button
                  size="xs"
                  variant="ghost"
                  color="red"
                  aria-label={`Remove ${holiday.name}`}
                  onClick={() => removeHoliday.mutate(holiday)}
                >
                  Remove
                </Button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="flex flex-col gap-3 rounded-lg border border-gray-200 p-4">
        <h3 className="text-sm font-semibold">Add from template</h3>
        <label className="flex flex-col gap-1 text-sm">
          Template
          <select
            aria-label="Holiday template"
            className="rounded-md border border-gray-300 px-3 py-2"
            value={templateId}
            onChange={(e) => setTemplateId(e.target.value)}
          >
            <option value="">Choose a country or region</option>
            {templates.map((template) => (
              <option key={template.id} value={template.id}>
                {template.name}
              </option>
            ))}
          </select>
        </label>
        {selectedTemplate && !selectedTemplate.years.includes(year) && (
          <p className="text-sm text-amber-700">
            This template has dates for {selectedTemplate.years.join(' and ')} only.
          </p>
        )}
        <OfficeScope
          value={templateOffices}
          onChange={setTemplateOffices}
          idPrefix="template-office"
        />
        <div>
          <Button
            color="red"
            disabled={
              !selectedTemplate ||
              !selectedTemplate.years.includes(year) ||
              addFromTemplate.isLoading
            }
            onClick={() => addFromTemplate.mutate()}
          >
            Add {selectedTemplate?.name ?? ''} holidays for {year}
          </Button>
        </div>
      </section>

      <form
        aria-label="Add a holiday"
        className="flex flex-col gap-3 rounded-lg border border-gray-200 p-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (!newHoliday.date || !newHoliday.name.trim()) {
            toast({ title: 'Enter a date and a name', variant: 'error' });
            return;
          }
          addHoliday.mutate();
        }}
      >
        <h3 className="text-sm font-semibold">Add a holiday</h3>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="flex flex-col gap-1 text-sm">
            Date
            <input
              type="date"
              className="rounded-md border border-gray-300 px-3 py-2"
              value={newHoliday.date}
              onChange={(e) => setNewHoliday({ ...newHoliday, date: e.target.value })}
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            Name
            <input
              className="rounded-md border border-gray-300 px-3 py-2"
              value={newHoliday.name}
              maxLength={100}
              onChange={(e) => setNewHoliday({ ...newHoliday, name: e.target.value })}
            />
          </label>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={newHoliday.half_day}
            onChange={(e) =>
              setNewHoliday({ ...newHoliday, half_day: e.target.checked })
            }
          />
          Half day
        </label>
        <OfficeScope
          value={newHoliday.office_ids}
          onChange={(ids) => setNewHoliday({ ...newHoliday, office_ids: ids })}
          idPrefix="holiday-office"
        />
        <div>
          <Button type="submit" color="red" disabled={addHoliday.isLoading}>
            Add holiday
          </Button>
        </div>
      </form>

      <section className="flex flex-col gap-3 rounded-lg border border-gray-200 p-4">
        <h3 className="text-sm font-semibold">Working days</h3>
        <p className="text-sm text-gray-500">
          Leave is only counted on these days.
        </p>
        <div className="flex flex-wrap gap-3">
          {WEEKDAYS.map((weekday) => (
            <label key={weekday.value} className="flex items-center gap-1 text-sm">
              <input
                type="checkbox"
                checked={workingDays.includes(weekday.value)}
                onChange={(e) =>
                  setWorkingDays(
                    e.target.checked
                      ? [...workingDays, weekday.value].sort()
                      : workingDays.filter((d) => d !== weekday.value),
                  )
                }
              />
              {weekday.label}
            </label>
          ))}
        </div>
        <div>
          <Button
            variant="outline"
            disabled={!workingDays.length || saveWorkingDays.isLoading}
            onClick={() => saveWorkingDays.mutate()}
          >
            Save working days
          </Button>
        </div>
      </section>
    </div>
  );
}
